import { useUserRoles } from '@anytag/auth';
import { SelectOption, SupportedSocialMediaType } from '@anytag/shared-types';
import { getSocialMediaText } from '@anytag/shared-utils';
import { CampaignIdentifierType, CampaignPostSocialType, CampaignType } from '@anytag/graphql';
import { useTranslation } from 'react-i18next';
import { useAllCategoryOptions } from './useAllCategoryOptions';
import { useAllAvailableCountryOptions } from './useAllAvailableCountryOptions';
import { useCampaignTypesOptionsWithPermissions } from './useCampaignTypesOptionsWithPermissions';

const SOCIAL_MEDIA_OPTIONS = [
  SupportedSocialMediaType.FACEBOOK,
  SupportedSocialMediaType.INSTAGRAM,
  SupportedSocialMediaType.INSTAGRAM_STORY,
  SupportedSocialMediaType.TWITTER,
  SupportedSocialMediaType.YOUTUBE,
  SupportedSocialMediaType.TIKTOK,
  SupportedSocialMediaType.DOUYIN,
  SupportedSocialMediaType.XHS,
  SupportedSocialMediaType.THREADS
].map((campaignType) => ({ label: getSocialMediaText(campaignType), value: campaignType }));

export const useReportFilterOptions = () => {
  const { isAdminStaff } = useUserRoles();
  const { t } = useTranslation();
  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const { allCategoryOptions } = useAllCategoryOptions();

  const allCampaignTypes = [
    { value: CampaignType.ENGAGEMENT, label: t('Engagement') },
    { value: CampaignType.MARKETPLACE, label: t('Marketplace') },
    ...(isAdminStaff ? [{ value: CampaignType.AUTO_MANAGED, label: t('eCommerce Affiliate') }] : [])
  ];
  const allCampaignIdentifierTypes = [
    { value: CampaignIdentifierType.ENGAGEMENT, label: t('Engagement') },
    { value: CampaignIdentifierType.MARKETPLACE, label: t('Marketplace') }
  ];
  const allowedCampaignsTypes = useCampaignTypesOptionsWithPermissions(allCampaignTypes);
  const socialMediaPostOptions: SelectOption<CampaignPostSocialType>[] = [
    { label: t<string>('Facebook'), value: CampaignPostSocialType.FACEBOOK },
    { label: t<string>('Facebook Page'), value: CampaignPostSocialType.FACEBOOK_PAGE },
    { label: t<string>('Instagram'), value: CampaignPostSocialType.INSTAGRAM },
    { label: t<string>('X(Twitter)'), value: CampaignPostSocialType.TWITTER },
    { label: t<string>('YouTube'), value: CampaignPostSocialType.YOUTUBE },
    { label: t<string>('TikTok'), value: CampaignPostSocialType.TIKTOK },
    { label: t<string>('Douyin'), value: CampaignPostSocialType.DOUYIN },
    { label: t<string>('Xiaohongshu'), value: CampaignPostSocialType.XHS },
    { label: t<string>('Threads'), value: CampaignPostSocialType.THREADS }
  ];

  return {
    isAdminStaff,
    allCampaignCategoryOptions: allCategoryOptions,
    allCampaignIdentifierTypes,
    allCampaignTypes: allowedCampaignsTypes,
    allCountryOptions: allAvailableCountryOptions,
    allSocialMediaOptions: SOCIAL_MEDIA_OPTIONS,
    allSocialMediaPostOptions: socialMediaPostOptions
  };
};
